import { useState } from 'react';

import { HelpOutline as HelpIcon, PictureAsPdf as PdfIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { downloadManual, listManuals, type ManualDto } from '@/lib/api/manuals';

function ManualRow({ manual }: { manual: ManualDto }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  const handleClick = async (): Promise<void> => {
    setBusy(true);
    setError(false);
    try {
      await downloadManual(manual.id, `${manual.title}.pdf`);
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={`${manual.title} PDF 다운로드`}
      onClick={() => void handleClick()}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          void handleClick();
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.5,
        border: 1,
        borderColor: error ? 'error.main' : 'divider',
        borderRadius: 1,
        cursor: busy ? 'default' : 'pointer',
        opacity: busy ? 0.6 : 1,
        '&:hover': { bgcolor: busy ? undefined : 'action.hover' },
      }}
    >
      {busy ? <CircularProgress size={26} /> : <PdfIcon sx={{ color: '#D81B60', fontSize: 28 }} />}
      <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600}>
          {manual.title}
        </Typography>
        <Typography variant="caption" color={error ? 'error.main' : 'text.disabled'}>
          {error ? '다운로드 실패 — 다시 시도해 주세요' : '클릭하여 PDF 다운로드'}
        </Typography>
      </Stack>
      <Chip size="small" label="PDF" variant="outlined" />
    </Box>
  );
}

/**
 * 의뢰서 4-1 도움말 진입점 — Header 우상단 IconButton (P10-4).
 *
 * 매뉴얼 원본은 docs/manuals/*.md 하나이고, PDF는 백엔드가 요청 시 생성해
 * 디스크에 캐시한다. 저장소에 바이너리를 두지 않기 위한 구조 (docs/manuals/README.md 참고).
 * 목록과 권한 판정도 서버가 하므로 프런트는 받은 목록을 그대로 그린다.
 */
export function HelpMenu() {
  const [open, setOpen] = useState(false);

  const query = useQuery({
    queryKey: ['manuals'],
    queryFn: listManuals,
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  return (
    <>
      <Tooltip title="도움말 / 매뉴얼">
        <IconButton color="inherit" onClick={() => setOpen(true)} aria-label="도움말">
          <HelpIcon />
        </IconButton>
      </Tooltip>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>도움말 / 매뉴얼</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {query.isLoading && (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={16} />
                <Typography variant="body2">불러오는 중…</Typography>
              </Stack>
            )}
            {query.isError && <Alert severity="error">매뉴얼 목록을 불러오지 못했습니다.</Alert>}
            {query.data?.map((m) => (
              <ManualRow key={m.id} manual={m} />
            ))}
            {query.data?.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                받을 수 있는 매뉴얼이 없습니다.
              </Typography>
            )}
            <Typography variant="caption" color="text.disabled">
              최신 내용이 자동으로 반영됩니다 · 추가 문의는 관리자에게 연락하세요.
            </Typography>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}
